import React, { useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { skipToken } from "@tanstack/react-query";
import { ArrowLeft, X } from "lucide-react";
import { getRelativePathFromFilePath } from "schemas/utils/file";

type Props = {
  funnelId: string;
  updateFunnelStepOrder: ({
    projectId,
    updatedOrder,
  }: {
    projectId: string;
    updatedOrder: string[];
  }) => void;
};

function FunnelPagesMenu({ funnelId, updateFunnelStepOrder }: Props) {
  const projectId = useCurrentProjectId();
  const [menuView, setMenuView] = useState<"page-list" | "add-existing">(
    "page-list",
  );
  const { data: funnel, refetch: refetchFunnel } = trpc.funnel.find.useQuery(
    {
      projectId: projectId ?? "",
      funnelId,
    },
    {
      enabled: Boolean(projectId && funnelId),
    },
  );

  const { data: projectPages = [] } = trpc.file.list.useQuery(
    projectId ? { projectId, fileType: "page" } : skipToken,
  );

  const { mutate: addFunnelStep } = trpc.funnel.addFunnelStep.useMutation({
    onSuccess: () => {
      void refetchFunnel();
    },
  });

  const { mutate: removeFunnelStep } = trpc.funnel.removeFunnelStep.useMutation(
    {
      onSuccess: () => {
        void refetchFunnel();
      },
    },
  );

  const [steps, setSteps] = useState(funnel?.funnelSteps ?? []);

  const existingPageIds = new Set(
    funnel?.funnelSteps.map((step) => step.fileId),
  );
  const availablePages = projectPages.filter(
    (page) => !existingPageIds.has(page.id),
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    let updatedSteps = steps;

    if (active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        updatedSteps = arrayMove(items, oldIndex, newIndex);
        setSteps(updatedSteps);
        return updatedSteps;
      });
    }
    updateFunnelStepOrder({
      projectId: projectId ?? "",
      updatedOrder: updatedSteps.map((step) => step.id),
    });
  };

  const handleAddPage = (pageId: string) => {
    addFunnelStep({
      projectId: projectId ?? "",
      fileId: pageId,
      funnelId,
    });
  };

  return (
    <div className="absolute top-5 right-0 flex flex-col bg-white border-l gap-3 border-t border-b border-border rounded-l-lg p-2 z-50 h-[500px] overflow-y-auto">
      {menuView === "page-list" && (
        <>
          <div className="text-default text-base font-semibold">
            Funnel pages
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={steps.map((step) => step.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="h-[500px] overflow-y-auto w-[250px] gap-3 flex flex-col">
                {steps.map((step) => (
                  <PageMenuNode
                    key={step.id}
                    id={step.id}
                    data={{
                      path: step.file?.path ?? "",
                      name: step.file?.name ?? "",
                    }}
                    onRemove={() => {
                      removeFunnelStep({
                        projectId: projectId ?? "",
                        stepId: step.id,
                      });
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <Button
            variant="secondary"
            size="base"
            onClick={() => setMenuView("add-existing")}
          >
            Add Page
          </Button>
        </>
      )}

      {menuView === "add-existing" && (
        <>
          <div className="flex flex-row items-center gap-2 w-[250px]">
            <IconButton
              variant="tertiary"
              icon={<ArrowLeft size={16} />}
              onClick={() => setMenuView("page-list")}
            />
            <div className="text-default text-base font-semibold">
              Add existing page
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {availablePages?.map((page) => (
              <div
                key={page.id}
                className="flex flex-col gap-1 border border-border rounded-lg p-2 cursor-pointer hover:shadow-sm transition-all duration-300"
                onClick={() => {
                  handleAddPage(page.id);
                }}
              >
                <div className="text-default text-base font-semibold">
                  {page.name ?? "Untitled"}
                </div>
                <div className="text-sm text-muted">
                  {getRelativePathFromFilePath(page.path)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const PageMenuNode = ({
  id,
  data,
  onRemove,
}: {
  id: string;
  data: { path: string; name: string };
  onRemove: () => void;
}) => {
  const { path, name } = data;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border flex flex-wrap rounded-lg flex-col h-fit p-1 bg-white w-full relative"
    >
      <div className="flex flex-row w-full justify-between">
        <div className="flex flex-col gap-1 w-full">
          <div className="flex flex-row justify-between w-full">
            <div {...attributes} {...listeners} className="flex flex-col gap-1">
              <div className="flex-1 cursor-move">
                <div className="text-default text-base font-semibold">
                  {name}
                </div>
              </div>
              <div className="text-sm text-muted mb-3">{path}</div>
            </div>
            <IconButton
              variant="tertiary"
              icon={<X size={16} />}
              onClick={handleRemove}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FunnelPagesMenu;
